import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Loader2, Clock, Mic, MicOff, Play, Square, ArrowLeft, ArrowRight, RotateCcw,
  Send, Sparkles, AlertCircle, CheckCircle2, Lightbulb, FileText,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import Seo from '@/components/shared/Seo';
import { ieltsApi, goToIeltsAuth } from '@/lib/ieltsApi';
import { toast } from 'sonner';
import WinhouseAIFeedbackBadge from '@/components/shared/WinhouseAIFeedbackBadge';

const fmt = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const wordCount = (txt) => (txt || '').trim().split(/\s+/).filter(Boolean).length;
const draftKey = (pid) => `ielts_speaking_draft_${pid}`;

const PART_LABEL = { part_1: 'Part 1 · Interview', part_2: 'Part 2 · Cue Card', part_3: 'Part 3 · Discussion' };

const BandRow = ({ label, value }) => (
  <div className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <span className="text-2xl font-bold text-black tabular-nums">{value ?? '—'}</span>
  </div>
);

const IeltsSpeakingPracticePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const part = (params.get('speaking_part') === 'part_2' || params.get('speaking_part') === 'part_3') ? params.get('speaking_part') : 'part_1';
  const promptIdParam = params.get('prompt_id') || '';

  const [mobile, setMobile] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [response, setResponse] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [prepSeconds, setPrepSeconds] = useState(0);
  const [phase, setPhase] = useState('prep'); // prep | speaking (part_2 only); always 'speaking' for P1/P3
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  // recording
  const [recording, setRecording] = useState(false);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recError, setRecError] = useState(null);
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const prepTimerRef = useRef(null);

  const target = part === 'part_2' ? 120 : (part === 'part_1' ? 240 : 300);
  const remaining = Math.max(0, target - seconds);
  const wc = useMemo(() => wordCount(response), [response]);
  const isPart2 = part === 'part_2';

  // 1) Auth + load prompt
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        await ieltsApi.authMe();
        if (!mounted) return;
        setAuthChecked(true);
        loadPrompt();
      } catch {
        if (!mounted) return;
        setAuthChecked(true); setNeedsLogin(true);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const loadPrompt = async () => {
    setLoading(true);
    setFeedback(null);
    setResponse(''); setSeconds(0); setPrepSeconds(0);
    setPhase(isPart2 ? 'prep' : 'speaking');
    stopRecording(false);
    setRecordedUrl(null);
    try {
      const qs = promptIdParam
        ? `?prompt_id=${encodeURIComponent(promptIdParam)}`
        : `?speaking_part=${part}`;
      const r = await ieltsApi.speakingNextPrompt(qs);
      setPrompt(r.prompt);
      try {
        const d = localStorage.getItem(draftKey(r.prompt.prompt_id));
        if (d) {
          const parsed = JSON.parse(d);
          setResponse(parsed.response || '');
          setSeconds(parsed.seconds || 0);
        }
      } catch { /* ignore quota */ }
    } catch (e) {
      toast.error(e.message || 'Could not load a prompt');
    } finally {
      setLoading(false);
    }
  };

  // 2) Part 2 prep timer
  useEffect(() => {
    if (!prompt || !isPart2 || phase !== 'prep' || feedback) return;
    prepTimerRef.current = setInterval(() => setPrepSeconds((s) => s + 1), 1000);
    return () => clearInterval(prepTimerRef.current);
  }, [prompt, isPart2, phase, feedback]);

  // Auto-advance prep → speaking after 60s
  useEffect(() => {
    if (isPart2 && phase === 'prep' && prepSeconds >= 60) {
      setPhase('speaking');
      toast.message('Prep time up — start speaking now!');
    }
  }, [isPart2, phase, prepSeconds]);

  // 3) Speaking timer (P1/P3 always running, P2 only after prep)
  useEffect(() => {
    if (!prompt || feedback) return;
    if (isPart2 && phase !== 'speaking') return;
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [prompt, feedback, isPart2, phase]);

  // 4) Autosave
  useEffect(() => {
    if (!prompt) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(draftKey(prompt.prompt_id), JSON.stringify({ response, seconds }));
        setSavedAt(new Date());
      } catch { /* ignore */ }
    }, 800);
    return () => clearTimeout(t);
  }, [response, seconds, prompt]);

  // Recording helpers ─────────────────────────────────────────────────────
  const startRecording = async () => {
    setRecError(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      setRecError('Recording is not supported in this browser. Use the text fallback below.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setRecordedUrl(url);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecRef.current = mr;
      mr.start();
      setRecording(true);
      toast.success('Recording — speak naturally');
    } catch (e) {
      setRecError(e.name === 'NotAllowedError'
        ? 'Microphone permission denied. You can still type your answer below.'
        : 'Could not access the microphone. Use the text fallback below.');
    }
  };
  const stopRecording = (notify = true) => {
    try {
      if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
        mediaRecRef.current.stop();
      }
    } catch { /* ignore */ }
    setRecording(false);
    if (notify) toast.message('Recording stopped — play it back or re-record.');
  };

  const handleSubmit = async () => {
    if (!prompt) return;
    if (wc < 20) {
      toast.info('Please type at least 20 words of your spoken response so the AI can give feedback.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await ieltsApi.speakingSubmitResponse({
        prompt_id: prompt.prompt_id,
        response_text: response,
        seconds_spent: seconds,
        has_recording: !!recordedUrl,
        request_ai_feedback: true,
      });
      setFeedback(r);
      try { localStorage.removeItem(draftKey(prompt.prompt_id)); } catch { /* ignore */ }
      toast.success('Submitted — AI band feedback below');
    } catch (e) { toast.error(e.message || 'Submission failed'); }
    finally { setSubmitting(false); }
  };

  if (!authChecked) {
    return (<div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-red-700" /></div>);
  }
  if (needsLogin) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center bg-white border border-gray-200 rounded-2xl p-8">
            <h1 className="text-2xl font-bold mb-2">Sign in to start speaking</h1>
            <Button onClick={() => goToIeltsAuth(`/ielts/speaking/practice?speaking_part=${part}`)}
              className="bg-black text-white rounded-full px-6 h-11">Sign in with Google</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="ielts-speaking-practice">
      <Seo title="IELTS Speaking Practice · Winhouse" canonical="/ielts/speaking/practice" />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-7xl mx-auto px-3 sm:px-6 pt-24 sm:pt-28 pb-8 w-full">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/ielts/speaking')}
              className="rounded-full" data-testid="speaking-prac-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Speaking Hub
            </Button>
            {prompt && (
              <>
                <Badge className="bg-black text-white">{PART_LABEL[prompt.speaking_part]}</Badge>
                <Badge variant="outline" className="capitalize">{prompt.topic}</Badge>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 text-sm">
            {isPart2 && phase === 'prep' && !feedback && (
              <div className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-mono tabular-nums ${
                prepSeconds >= 50 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-blue-50 text-blue-700 border border-blue-200'
              }`} data-testid="speaking-prac-prep-timer">
                Prep · {fmt(60 - prepSeconds)}
              </div>
            )}
            {(!isPart2 || phase === 'speaking') && !feedback && (
              <div className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full font-mono tabular-nums ${
                remaining === 0 ? 'bg-red-50 text-red-700 border border-red-200'
                : remaining < 30 ? 'bg-amber-50 text-amber-700 border border-amber-200'
                : 'bg-white text-black border border-gray-200'
              }`} data-testid="speaking-prac-timer">
                <Clock className="h-4 w-4" /> {fmt(remaining)}
              </div>
            )}
            {savedAt && !feedback && (
              <span className="hidden sm:inline text-xs text-gray-500" data-testid="speaking-prac-saved">Saved</span>
            )}
          </div>
        </div>

        {loading || !prompt ? (
          <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
            <Loader2 className="h-7 w-7 mx-auto animate-spin text-red-700" />
            <p className="text-sm text-gray-500 mt-3">Loading prompt…</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {/* LEFT — prompt / cue card */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 max-h-[calc(100vh-200px)] overflow-auto"
              data-testid="speaking-prac-prompt-pane">
              <div className="text-xs uppercase tracking-[0.18em] text-red-700 font-semibold mb-3">
                {PART_LABEL[prompt.speaking_part]}
                {prompt.topic ? <span className="ml-2 text-gray-500 normal-case tracking-normal">· {prompt.topic.replace(/_/g, ' ')}</span> : null}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold leading-snug mb-3" data-testid="speaking-prac-title">
                {prompt.subtopic ? prompt.subtopic : (prompt.prompt_text || '').split('\n')[0]}
              </h2>

              {prompt.speaking_part === 'part_2' && prompt.cue_card_text ? (
                <div className="bg-[#FAF9F6] border-2 border-dashed border-gray-300 rounded-2xl p-5 sm:p-6 mb-4" data-testid="speaking-prac-cue-card">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold mb-2 flex items-center gap-1.5">
                    <FileText className="h-3 w-3" /> Cue Card
                  </div>
                  <div className="whitespace-pre-wrap font-serif text-[15px] leading-7 text-gray-900">{prompt.cue_card_text}</div>
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-[15px] leading-7 text-gray-900 font-serif mb-4" data-testid="speaking-prac-prompt-text">
                  {prompt.prompt_text}
                </div>
              )}

              {Array.isArray(prompt.follow_up_questions) && prompt.follow_up_questions.length > 0 && prompt.speaking_part !== 'part_2' && (
                <div className="mt-2" data-testid="speaking-prac-followups">
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Follow-up questions</div>
                  <ul className="space-y-1.5">
                    {prompt.follow_up_questions.map((q, i) => (
                      <li key={i} className="text-sm leading-6 flex gap-2"><span className="text-red-700 flex-shrink-0">→</span><span>{q}</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {prompt.speaking_part === 'part_2' && prompt.follow_up_questions?.[0] && (
                <div className="mt-3 text-sm text-gray-600">
                  <strong className="font-semibold">After 2 min, examiner may ask:</strong> {prompt.follow_up_questions[0]}
                </div>
              )}

              <div className="mt-5 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800">
                <Lightbulb className="h-3.5 w-3.5" />
                {isPart2 ? '60 sec prep · then speak for up to 2 min' : 'Speak naturally — examiner-style timing'}
              </div>
            </section>

            {/* RIGHT — recording + transcript + submit */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 sm:p-7 flex flex-col"
              data-testid="speaking-prac-record-pane">
              {isPart2 && phase === 'prep' && !feedback ? (
                <div className="text-center py-8" data-testid="speaking-prac-prep-state">
                  <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
                    <Lightbulb className="h-6 w-6 text-blue-700" />
                  </div>
                  <h3 className="font-bold text-lg mb-1">Preparation time — 1 minute</h3>
                  <p className="text-sm text-gray-600 mb-5">Read the cue card. Jot down notes. The speaking timer starts automatically.</p>
                  <textarea value={response} onChange={(e) => setResponse(e.target.value)}
                    placeholder="Notes (optional — these can also be your spoken transcript later)…"
                    rows={6}
                    className="w-full border border-gray-200 rounded-xl p-3 text-sm leading-6 font-serif focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none"
                    data-testid="speaking-prac-notes" />
                  <Button onClick={() => setPhase('speaking')} className="mt-4 bg-black text-white rounded-full px-6 h-11"
                    data-testid="speaking-prac-start-speaking">
                    Skip prep · Start speaking now <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              ) : (
                <>
                  <h3 className="font-bold text-lg mb-3">Record or type your response</h3>

                  <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="speaking-prac-recorder">
                    {!recording ? (
                      <Button onClick={startRecording} disabled={!!feedback}
                        className="bg-red-700 hover:bg-red-800 text-white rounded-full h-11 px-5" data-testid="speaking-prac-start-rec">
                        <Mic className="h-4 w-4 mr-1.5" /> {recordedUrl ? 'Re-record' : 'Start recording'}
                      </Button>
                    ) : (
                      <Button onClick={() => stopRecording()} className="bg-black text-white rounded-full h-11 px-5"
                        data-testid="speaking-prac-stop-rec">
                        <Square className="h-4 w-4 mr-1.5" /> Stop
                      </Button>
                    )}
                    {recording && <span className="inline-flex items-center gap-1.5 text-xs text-red-700 font-medium">
                      <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> Recording…
                    </span>}
                    {recordedUrl && !recording && (
                      <audio src={recordedUrl} controls className="h-9" data-testid="speaking-prac-playback" />
                    )}
                  </div>
                  {recError && (
                    <div className="mb-3 text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1.5">
                      <MicOff className="h-3.5 w-3.5" /> {recError}
                    </div>
                  )}

                  <label className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    Your transcript {recordedUrl ? '(type or paraphrase what you said)' : '(typed answer fallback)'}
                  </label>
                  <textarea value={response} onChange={(e) => setResponse(e.target.value)} disabled={!!feedback}
                    rows={10} placeholder="Type or paraphrase your spoken answer here. The AI will score it against IELTS Speaking band descriptors."
                    className="flex-1 w-full resize-y border border-gray-200 rounded-xl p-4 text-[14px] leading-7 font-serif focus:border-red-600 focus:ring-1 focus:ring-red-600 outline-none disabled:bg-gray-50"
                    data-testid="speaking-prac-transcript" />
                  <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-mono tabular-nums ${
                      wc >= 20 ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-600 border border-gray-200'
                    }`}>{wc} words {wc < 20 ? '· min 20' : ''}</span>
                    {remaining === 0 && !feedback && (
                      <span className="text-xs text-red-700 inline-flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" /> Time up — submit now</span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <Button onClick={handleSubmit} disabled={submitting || !!feedback}
                      className="bg-black text-white rounded-full px-6 h-11" data-testid="speaking-prac-submit">
                      {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Evaluating…</>
                        : <><Send className="h-4 w-4 mr-1.5" /> Submit for AI Feedback</>}
                    </Button>
                    <Button variant="outline" onClick={loadPrompt} disabled={submitting} className="rounded-full h-11" data-testid="speaking-prac-skip">
                      <RotateCcw className="h-4 w-4 mr-1.5" /> Skip / new prompt
                    </Button>
                  </div>
                </>
              )}
            </section>
          </div>
        )}

        {feedback && <FeedbackPanel feedback={feedback} onNew={loadPrompt} onDashboard={() => navigate('/ielts/speaking/dashboard')} />}
      </main>
    </div>
  );
};

const FeedbackPanel = ({ feedback, onNew, onDashboard }) => {
  const ai = feedback.ai_feedback || {};
  const bands = ai.bands || {};
  const overall = feedback.estimated_band_overall ?? ai.estimated_band_overall;
  const error = typeof ai.error === 'string' ? ai.error : null;
  return (
    <section className="mt-6 bg-white border border-gray-200 rounded-2xl p-5 sm:p-7" data-testid="speaking-prac-feedback">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-red-700" />
          <h3 className="font-bold text-lg">AI Band Feedback</h3>
          <WinhouseAIFeedbackBadge model={ai.model || 'claude-sonnet-4-5'} className="ml-1" />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onDashboard} className="rounded-full">View dashboard</Button>
          <Button onClick={onNew} className="bg-black text-white rounded-full">Try another prompt</Button>
        </div>
      </div>
      {error ? (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 inline mr-1.5" />{error}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <div className="bg-black text-white rounded-2xl p-5 flex flex-col justify-center">
              <span className="text-xs uppercase tracking-wider text-white/70 mb-1">Overall</span>
              <span className="text-4xl font-bold text-[#D3D86F]">{overall ?? '—'}</span>
              <span className="text-xs text-white/60 mt-1">Estimated band</span>
            </div>
            <BandRow label="Fluency & Coherence" value={bands.fluency_coherence} />
            <BandRow label="Lexical Resource" value={bands.lexical_resource} />
            <BandRow label="Grammar Range & Accuracy" value={bands.grammatical_range_accuracy} />
            <BandRow label="Pronunciation*" value={bands.pronunciation} />
          </div>
          {ai.pronunciation_note && (
            <div className="text-xs text-gray-500 italic mb-4 -mt-3">*{ai.pronunciation_note}</div>
          )}
          {ai.summary && (
            <div className="mb-5 bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm leading-7">
              <strong className="font-semibold">Examiner summary: </strong>{ai.summary}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3 mb-5">
            {Array.isArray(ai.strengths) && ai.strengths.length > 0 && (
              <div className="border border-green-200 bg-green-50/50 rounded-xl p-4 text-sm leading-7">
                <div className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-1.5">Strengths</div>
                <ul className="space-y-1">
                  {ai.strengths.map((s, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-green-700 mt-1.5 flex-shrink-0" />{s}</li>)}
                </ul>
              </div>
            )}
            {Array.isArray(ai.improvements) && ai.improvements.length > 0 && (
              <div className="border border-red-200 bg-red-50/40 rounded-xl p-4 text-sm leading-7">
                <div className="text-xs uppercase tracking-wider text-red-700 font-semibold mb-1.5">Improvements</div>
                <ul className="space-y-1">
                  {ai.improvements.map((s, i) => <li key={i} className="flex gap-2"><AlertCircle className="h-3.5 w-3.5 text-red-700 mt-1.5 flex-shrink-0" />{s}</li>)}
                </ul>
              </div>
            )}
          </div>
          {Array.isArray(ai.vocabulary_upgrades) && ai.vocabulary_upgrades.length > 0 && (
            <div className="mb-5">
              <h4 className="font-semibold mb-2 text-sm uppercase tracking-wider text-gray-500">Vocabulary upgrades</h4>
              <div className="flex flex-wrap gap-2">
                {ai.vocabulary_upgrades.map((v, i) => (
                  <span key={i} className="px-3 py-1 bg-[#D3D86F]/20 border border-[#D3D86F] rounded-full text-xs font-medium">{v}</span>
                ))}
              </div>
            </div>
          )}
          {ai.improved_sample_answer && (
            <div className="bg-black text-white rounded-2xl p-5 mb-3" data-testid="speaking-ai-rewrite">
              <div className="text-xs uppercase tracking-wider text-[#D3D86F] font-semibold mb-2">Band 8 sample answer</div>
              <div className="text-sm leading-7 font-serif text-white/90 whitespace-pre-wrap">{ai.improved_sample_answer}</div>
            </div>
          )}
          {ai.next_practice_recommendation && (
            <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <strong className="font-semibold text-amber-900">Next practice: </strong>{ai.next_practice_recommendation}
            </div>
          )}
        </>
      )}
      <WinhouseAIFeedbackBadge variant="footer" model={ai.model || 'claude-sonnet-4-5'} />
    </section>
  );
};

export default IeltsSpeakingPracticePage;
